import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Globe, Network, ShieldCheck, TimerReset, Trophy, Zap } from "lucide-react";
import { OtrosBonos, otrosDraftFrom, type OtrosDraft } from "./OtrosBonos";
import type { CommissionParams } from "@nh/shared";
import { PageHeader } from "@/shared/ui/layout";
import { Alert, Button, Checkbox, Input } from "@/shared/ui/core";
import { SkeletonCard } from "@/shared/ui/ui";
import { cn } from "@/shared/lib/cn";
import { AdminGuard } from "@/features/admin/components";
import { getCommissionParams, updateCommissionParams } from "./commissions.api";

/**
 * ═══════════════════════════════════════════════════════════════════
 * Comisiones — la TABLA de gradiente del sistema viejo, que es lo que
 * Anahí pidió replicar (27-ago: "aquí sí tenemos una tabla linda"):
 * pestañas por bono, el interruptor y sus parámetros en una fila, y la
 * tabla con encabezado OSCURO — "Gradiente · N niveles" a la izquierda,
 * el chip `Σ = X%` a la derecha — con NIVEL · % · POSICIÓN EN LA RED.
 *
 * Sin manuales ni targets: se modifican las comisiones y ya.
 * ═══════════════════════════════════════════════════════════════════
 */
export function AdminCommissionParamsPage() {
  return (
    <AdminGuard>
      <ParamsScreen />
    </AdminGuard>
  );
}

type BonusKey = "bir" | "bur";
/*
 * Los bonos SIN gradiente (Anahí, 29-ago: "¿dónde están las demás?").
 * Estaban en el plan desde el principio —el contrato los guarda y el
 * servidor los valida— pero la pantalla solo pintaba los tres que
 * tienen niveles, y los demás quedaban invisibles.
 */
type OtroKey = "bar" | "fondos" | "rule4060" | "reloj";
type TabKey = BonusKey | OtroKey;

const BONUS_TABS: {
  key: BonusKey;
  label: string;
  icon: typeof Zap;
  /** El encabezado de la columna del %: sobre QUÉ se aplica. */
  baseLabel: string;
}[] = [
  { key: "bir", label: "BIR · Inicio rápido", icon: Zap, baseLabel: "% sobre primera compra" },
  { key: "bur", label: "BUR · Bono de red", icon: Network, baseLabel: "% sobre volumen" },
];

const OTRAS_TABS: { key: OtroKey; label: string; icon: typeof Zap }[] = [
  { key: "bar", label: "BAR · Rango", icon: Trophy },
  { key: "fondos", label: "Fondos", icon: Globe },
  { key: "rule4060", label: "Regla 40/60", icon: ShieldCheck },
  /*
   * El reloj de recompra, en su pestaña (Anahí, 29-ago). Colgaba en una
   * card suelta bajo la tabla: no es un bono, pero SÍ es un ajuste del
   * plan que se guarda con el mismo botón, y ahí abajo parecía otra
   * pantalla pegada al final.
   */
  { key: "reloj", label: "Reloj de recompra", icon: TimerReset },
];

const ES_OTRO = (key: TabKey): key is OtroKey =>
  OTRAS_TABS.some((item) => item.key === key);

/** "Tu inscrito directo" / "N nivel(es) bajo tu inscrito" — la columna
 * de posición de la referencia, que es lo que hace legible el gradiente. */
function positionLabel(level: number): string {
  if (level === 1) return "Tu inscrito directo";
  return `${level - 1} nivel(es) bajo tu inscrito`;
}

interface BonusDraft {
  enabled: boolean;
  levels: string[];
  /** Solo BIR: la ventana del primer mes, en días. */
  eligibilityDays?: string;
}

function draftFrom(params: CommissionParams, key: BonusKey): BonusDraft {
  return {
    enabled: params[key].enabled,
    levels: params[key].levels.map((level) => String(level.bps / 100)),
    ...(key === "bir" ? { eligibilityDays: String(params.bir.eligibilityDays) } : {}),
  };
}

function ParamsScreen() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const params = useQuery({
    queryKey: ["admin", "commission-params"],
    queryFn: getCommissionParams,
  });

  const [tab, setTab] = useState<TabKey>("bir");
  const [drafts, setDrafts] = useState<Record<BonusKey, BonusDraft> | null>(null);
  const [otros, setOtros] = useState<OtrosDraft | null>(null);
  /* El reloj de recompra: ciclo y aviso, como texto mientras se edita. */
  const [clock, setClock] = useState<{ cycleDays: string; expiringSoonDays: string; activationVolumePoints: string } | null>(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (params.data && !drafts) {
      setDrafts({
        bir: draftFrom(params.data, "bir"),
        bur: draftFrom(params.data, "bur"),
      });
      setOtros(otrosDraftFrom(params.data));
      setClock({
        cycleDays: String(params.data.membership.cycleDays),
        expiringSoonDays: String(params.data.membership.expiringSoonDays),
        activationVolumePoints: String(params.data.membership.activationVolumePoints),
      });
    }
  }, [params.data, drafts]);

  const guardar = useMutation({
    mutationFn: updateCommissionParams,
    onSuccess: (fresh) => {
      queryClient.setQueryData(["admin", "commission-params"], fresh);
      setSaved(true);
    },
    onError: (cause) =>
      setError(cause instanceof Error ? cause.message : t("No se pudo guardar")),
  });

  function setBonus(key: BonusKey, update: Partial<BonusDraft>) {
    setSaved(false);
    setDrafts((current) =>
      current ? { ...current, [key]: { ...current[key], ...update } } : current,
    );
  }

  function enviar() {
    if (!params.data || !drafts || !clock) return;
    setError(null);

    const cycleDays = Number(clock.cycleDays);
    const expiringSoonDays = Number(clock.expiringSoonDays);
    if (!Number.isInteger(cycleDays) || cycleDays < 1 || cycleDays > 365) {
      setError(t("El ciclo va de 1 a 365 días"));
      return;
    }
    const activationVolumePoints = Number(clock.activationVolumePoints);
    if (!Number.isInteger(activationVolumePoints) || activationVolumePoints < 0) {
      setError(t("Los puntos para ser socio activo son un entero, 0 o más"));
      return;
    }
    if (!Number.isInteger(expiringSoonDays) || expiringSoonDays < 0 || expiringSoonDays >= cycleDays) {
      setError(t("El aviso tiene que ser más corto que el ciclo"));
      return;
    }

    for (const key of ["bir", "bur"] as const) {
      const bad = drafts[key].levels.find((value) => {
        const pct = Number(value);
        return !Number.isFinite(pct) || pct < 0 || pct > 100;
      });
      if (bad !== undefined) {
        setError(t("Los porcentajes van de 0 a 100"));
        return;
      }
    }
    const days = Number(drafts.bir.eligibilityDays);
    if (!Number.isInteger(days) || days < 1 || days > 365) {
      setError(t("El primer mes va de 1 a 365 días"));
      return;
    }

    const fresh = params.data;
    const section = <K extends BonusKey>(key: K): CommissionParams[K] => {
      const draft = drafts[key];
      return {
        ...fresh[key],
        enabled: draft.enabled,
        levels: fresh[key].levels.map((level, index) => ({
          ...level,
          bps: Math.round(Number(draft.levels[index] ?? 0) * 100),
        })),
      };
    };

    /*
     * Los bonos sin gradiente se guardan por el MISMO endpoint: el
     * esquema del plan ya los llevaba dentro, solo faltaba mandarlos.
     * Los catálogos que dependen del rango (hitos del BAR, tramos de
     * lealtad, los tres fondos) viajan intactos: se editan donde viven
     * sus rangos, no aquí.
     */
    const pct = (value: string) => Math.round((Number(value) || 0) * 100);
    const entero = (value: string) => Math.max(0, Math.round(Number(value) || 0));

    guardar.mutate({
      bir: { ...section("bir"), eligibilityDays: days },
      bur: section("bur"),
      membership: { cycleDays, expiringSoonDays, activationVolumePoints },
      ...(otros
        ? {
            bar: {
              ...fresh.bar,
              enabled: otros.barEnabled,
              fundBpsOfCommissionableVolume: pct(otros.barFundPct),
              requiredQualifiedPriorMonths: Math.min(12, entero(otros.barPriorMonths)),
            },
            globalFunds: { ...fresh.globalFunds, enabled: otros.fundsEnabled },
            rule4060: {
              ...fresh.rule4060,
              enabled: otros.rule4060Enabled,
              minPersonalVolumePoints: entero(otros.rule4060MinVp),
              minExternalClients: Math.min(100, entero(otros.rule4060MinClients)),
            },
          }
        : {}),
    });
  }

  const esOtro = ES_OTRO(tab);
  const active = BONUS_TABS.find((item) => item.key === tab) ?? BONUS_TABS[0]!;
  const draft = esOtro ? drafts?.bir : drafts?.[tab as BonusKey];
  const sum = draft
    ? draft.levels.reduce((total, value) => total + (Number(value) || 0), 0)
    : 0;

  return (
    <>
      {/*
        El botón de guardar, ARRIBA a la derecha (Anahí, 29-ago). Estaba
        al final de la página: con siete pestañas y una tabla de cinco
        niveles, había que bajar hasta el fondo para guardar un cambio
        hecho en la primera línea. Aquí está a la vista siempre, con el
        cambio y el botón en la misma pantalla.
      */}
      <PageHeader
        titleHidden
        title={t("Comisiones")}
        description={t("Los porcentajes del plan, por nivel. Aplican solo a comisiones nuevas.")}
        actions={
          <Button loading={guardar.isPending} onClick={enviar} disabled={!drafts || !clock}>
            {t("Guardar cambios")}
          </Button>
        }
      />

      {params.isPending || !draft || !clock ? (
        <SkeletonCard />
      ) : (
        <div className="grid gap-4">
          {/*
            UNA card (Anahí, 27-ago): la barra de pestañas pegada arriba
            —como la referencia—, la fila de configuración DENTRO, y la
            tabla de gradiente debajo. Nada flota suelto.
          */}
          <div className="overflow-hidden rounded-3xl border border-ds-border bg-white shadow-ds-float">
            {/* ── Las pestañas, integradas al borde superior ──────────── */}
            <div
              role="tablist"
              aria-label={t("Bonos del plan")}
              className="flex flex-wrap border-b border-ds-border bg-ds-cream-2/50"
            >
              {[...BONUS_TABS, ...OTRAS_TABS].map((item) => (
                <button
                  key={item.key}
                  role="tab"
                  aria-selected={tab === item.key}
                  onClick={() => setTab(item.key)}
                  className={cn(
                    "relative inline-flex items-center gap-2 px-5 py-3.5 text-[13px] font-semibold transition",
                    tab === item.key
                      ? "bg-white text-ds-violet-pop after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:bg-ds-violet-pop"
                      : "text-ds-ink-3 hover:bg-white/60 hover:text-ds-ink",
                  )}
                >
                  <item.icon className="h-4 w-4" />
                  {t(item.label)}
                </button>
              ))}
            </div>

            {tab === "reloj" ? (
              /*
                El reloj vive con los bonos porque se guarda con ellos:
                mismo plan, mismo botón. No es un bono, y por eso no
                lleva gradiente ni casilla de habilitado — el ciclo
                siempre corre.
              */
              <div className="grid gap-4 p-5">
                <p className="rounded-2xl bg-ds-lavender-bg/60 px-4 py-3 text-[12.5px] leading-relaxed text-ds-ink-2">
                  {t(
                    "Cada pedido validado reinicia el reloj del comprador con este ciclo. Aplica a las validaciones nuevas.",
                  )}
                </p>
                <div className="flex flex-wrap gap-5">
                  <label className="grid gap-1 text-[12.5px] font-medium text-ds-ink-3">
                    {t("Ciclo de recompra (días)")}
                    <div className="w-40">
                      <Input
                        type="number"
                        min={1}
                        max={365}
                        value={clock.cycleDays}
                        onChange={(event) => {
                          setSaved(false);
                          setClock({ ...clock, cycleDays: event.target.value });
                        }}
                      />
                    </div>
                  </label>
                  <label className="grid gap-1 text-[12.5px] font-medium text-ds-ink-3">
                    {t("Aviso «por vencer» (días antes)")}
                    <div className="w-40">
                      <Input
                        type="number"
                        min={0}
                        max={364}
                        value={clock.expiringSoonDays}
                        onChange={(event) => {
                          setSaved(false);
                          setClock({ ...clock, expiringSoonDays: event.target.value });
                        }}
                      />
                    </div>
                  </label>
                  {/*
                    ── Socio activo (Anahí, 14-sep-2026) ───────────────────
                    Los PV propios dentro del ciclo para cobrar comisiones.
                    Con menos, ese mes es cliente: mismo precio, sin bonos.
                  */}
                  <label className="grid gap-1 text-[12.5px] font-medium text-ds-ink-3">
                    {t("PV al mes para ser socio activo")}
                    <div className="w-40">
                      <Input
                        type="number"
                        min={0}
                        value={clock.activationVolumePoints}
                        onChange={(event) => {
                          setSaved(false);
                          setClock({ ...clock, activationVolumePoints: event.target.value });
                        }}
                      />
                    </div>
                  </label>
                </div>
                <p className="mt-3 text-[12.5px] text-ds-ink-3">
                  {t("Quien no junta esos puntos con sus propias compras dentro del ciclo es cliente ese mes: paga precio de socio, pero no cobra comisiones de su red. Aplica igual al franquiciatario.")}
                </p>
              </div>
            ) : esOtro && otros && params.data ? (
              <OtrosBonos
                tab={tab as Exclude<OtroKey, "reloj">}
                params={params.data}
                draft={otros}
                onChange={(update) => {
                  setSaved(false);
                  setOtros((current) => (current ? { ...current, ...update } : current));
                }}
              />
            ) : (
              <>
            {/* ── La configuración del bono, dentro de la card ────────── */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 border-b border-ds-border px-5 py-4">
              <Checkbox
                label={t("{{bono}} habilitado", { bono: active.label.split(" ·")[0] })}
                checked={draft.enabled}
                onChange={(event) => setBonus(tab as BonusKey, { enabled: event.target.checked })}
              />
              {tab === "bir" && (
                <label className="flex items-center gap-2 text-[13px] font-medium text-ds-ink-3">
                  {t("Primer mes (días)")}
                  <div className="w-24">
                    <Input
                      type="number"
                      min={1}
                      max={365}
                      value={draft.eligibilityDays ?? ""}
                      onChange={(event) =>
                        setBonus("bir", { eligibilityDays: event.target.value })
                      }
                    />
                  </div>
                </label>
              )}
              <p className="ml-auto text-[12px] text-ds-ink-3">
                {t("Afecta a las comisiones de TODA la red, solo hacia adelante.")}
              </p>
            </div>

            {/* ── El gradiente: encabezado oscuro y chip Σ ────────────── */}
            <div className="flex flex-wrap items-center justify-between gap-3 bg-ds-ink px-5 py-3.5">
              <h2 className="text-[14px] font-semibold text-white">
                {t("Gradiente {{bono}} · {{n}} niveles (N1 → N{{n}})", {
                  bono: active.label.split(" ·")[0],
                  n: draft.levels.length,
                })}
              </h2>
              <span className="rounded-lg bg-white/10 px-3 py-1 font-mono text-[12px] font-semibold text-emerald-300">
                Σ = {Math.round(sum * 10) / 10}%
              </span>
            </div>

            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-ds-border bg-ds-cream-2/40">
                  <th className="px-5 py-3 text-[11px] font-bold uppercase tracking-ds-label text-ds-ink-3">
                    {t("Nivel")}
                  </th>
                  <th className="px-5 py-3 text-center text-[11px] font-bold uppercase tracking-ds-label text-ds-ink-3">
                    {t(active.baseLabel)}
                  </th>
                  <th className="px-5 py-3 text-[12px] font-semibold text-ds-ink-3">
                    {t("Posición en la red")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {draft.levels.map((value, index) => (
                  <tr key={index} className="border-b border-ds-border last:border-0">
                    <td className="px-5 py-3.5 text-[14px] font-medium text-ds-ink">
                      {t("Nivel {{n}}", { n: index + 1 })}
                    </td>
                    <td className="px-5 py-2.5 text-center">
                      <label className="inline-flex items-center gap-1 rounded-xl border border-ds-border bg-white px-3 py-1.5">
                        <input
                          type="number"
                          min={0}
                          max={100}
                          step="0.5"
                          value={value}
                          aria-label={t("Porcentaje del nivel {{n}}", { n: index + 1 })}
                          onChange={(event) =>
                            setBonus(tab as BonusKey, {
                              levels: draft.levels.map((current, i) =>
                                i === index ? event.target.value : current,
                              ),
                            })
                          }
                          className="w-12 bg-transparent text-right font-mono text-[14px] font-semibold text-ds-ink outline-none"
                        />
                        <span className="text-[13px] text-ds-ink-3">%</span>
                      </label>
                    </td>
                    <td className="px-5 py-3.5 text-[13px] text-ds-ink-3">
                      {t(positionLabel(index + 1))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
              </>
            )}
          </div>


          {error && (
            <Alert variant="error" onDismiss={() => setError(null)}>
              {error}
            </Alert>
          )}
          {saved && (
            <Alert variant="success" onDismiss={() => setSaved(false)}>
              {t("Guardado. Las comisiones nuevas ya usan estos porcentajes.")}
            </Alert>
          )}

        </div>
      )}
    </>
  );
}
